import { TypedBaseManagerNode } from "./_Base";
import { NodeContext } from "../../poly/NodeContext";
import { NodeParamsConfig } from "../utils/params/ParamsConfig";
import { ROOT_NAME } from "../../scene/utils/ObjectsController";
import { Scene } from "three";
import { RootAudioController, RootAudioParamConfig } from "./utils/Scene/Audio";
import { SceneAutoUpdateParamConfig, SceneAutoUpdateController } from "./utils/Scene/AutoUpdate";
import { SceneBackgroundParamConfig, SceneBackgroundController } from "./utils/Scene/Background";
import { SceneEnvParamConfig, SceneEnvController } from "./utils/Scene/Env";
import { SceneFogParamConfig, SceneFogController } from "./utils/Scene/Fog";
import { RootLoadProgressParamConfig, RootLoadProgressController } from "./utils/Scene/LoadProgress";
import { SceneMaterialOverrideParamConfig, SceneMaterialOverrideController } from "./utils/Scene/MaterialOverride";
import { RootMainCameraController, RootMainCameraParamConfig } from "./utils/Scene/RootMainCamera";
export const ROOT_NODE_NAME = "RootNode";
class ObjectsManagerParamsConfig extends RootLoadProgressParamConfig(RootAudioParamConfig(SceneMaterialOverrideParamConfig(SceneEnvParamConfig(SceneFogParamConfig(RootMainCameraParamConfig(SceneBackgroundParamConfig(SceneAutoUpdateParamConfig(NodeParamsConfig)))))))) {
}
const ParamsConfig = new ObjectsManagerParamsConfig();
export class RootManagerNode extends TypedBaseManagerNode {
  constructor() {
    super(...arguments);
    this.paramsConfig = ParamsConfig;
    this._object = this._createScene();
    this._queued_nodes_by_id = /* @__PURE__ */ new Map();
    this.audioController = new RootAudioController(this);
    this.sceneAutoUpdateController = new SceneAutoUpdateController(this);
    this.sceneBackgroundController = new SceneBackgroundController(this);
    this.sceneEnvController = new SceneEnvController(this);
    this.sceneFogController = new SceneFogController(this);
    this.loadProgress = new RootLoadProgressController(this);
    this.sceneMaterialOverrideController = new SceneMaterialOverrideController(this);
    this.mainCameraController = new RootMainCameraController(this);
    this._childrenControllerContext = NodeContext.OBJ;
  }
  static type() {
    return "root";
  }
  cook() {
    this.cookController.endCook();
  }
  initializeNode() {
    this._object.matrixAutoUpdate = false;
    this.lifecycle.onChildAdd(this._on_child_add.bind(this));
    this.lifecycle.onChildRemove(this._on_child_remove.bind(this));
  }
  _createScene() {
    const scene = new Scene();
    scene.name = ROOT_NAME;
    scene.matrixAutoUpdate = false;
    return scene;
  }
  get object() {
    return this._object;
  }
  createNode(nodeClass, options) {
    return super.createNode(nodeClass, options);
  }
  children() {
    return super.children();
  }
  nodesByType(type) {
    return super.nodesByType(type);
  }
  _updateScene() {
    this.sceneAutoUpdateController.update();
    this.sceneBackgroundController.update();
    this.sceneEnvController.update();
    this.sceneFogController.update();
    this.sceneMaterialOverrideController.update();
  }
  _addToQueue(node) {
    const id = node.graphNodeId();
    if (!this._queued_nodes_by_id.has(id)) {
      this._queued_nodes_by_id.set(id, node);
    }
    return node;
  }
  processQueue() {
    this._updateScene();
    const queued_nodes_by_path = /* @__PURE__ */ new Map();
    const paths = [];
    this._queued_nodes_by_id.forEach((node, id) => {
      const fullPath = `_____${node.renderOrder}__${node.path()}`;
      paths.push(fullPath);
      queued_nodes_by_path.set(fullPath, node);
    });
    this._queued_nodes_by_id.clear();
    for (let path_id of paths) {
      const node = queued_nodes_by_path.get(path_id);
      if (node) {
        queued_nodes_by_path.delete(path_id);
        this._addToScene(node);
      }
    }
  }
  _update_object(node) {
    if (!this.scene().loadingController.autoUpdating()) {
      return this._addToQueue(node);
    } else {
      if (node.isDisplayed() && !node.cookController.isCooking()) {
        node.compute();
      }
      return this._addToScene(node);
    }
  }
  getParentForNode(node) {
    if (node.attachableToHierarchy()) {
      const node_input = node.io.inputs.input(0);
      if (node_input) {
        return node_input.childrenGroup();
      } else {
        return this._object;
      }
    } else {
      return null;
    }
  }
  _addToScene(node) {
    var _a;
    if (node.attachableToHierarchy()) {
      const parent_object = this.getParentForNode(node);
      if (parent_object) {
        if (node.usedInScene()) {
          (_a = node.childrenDisplayController) == null ? void 0 : _a.request_display_node_container();
          node.addObjectToParent(parent_object);
        } else {
          node.removeObjectFromParent();
        }
      } else {
      }
    }
  }
  _removeFromScene(node) {
    node.removeObjectFromParent();
  }
  areChildrenCooking() {
    const children = this.children();
    for (let child of children) {
      if (child.cookController.isCooking() || child.isDisplayNodeCooking()) {
        return true;
      }
    }
    return false;
  }
  addToParentTransform(node) {
    this._update_object(node);
  }
  removeFromParentTransform(node) {
    this._update_object(node);
  }
  _on_child_add(node) {
    if (node) {
      this._update_object(node);
    }
  }
  _on_child_remove(node) {
    if (node) {
      this._removeFromScene(node);
    }
  }
}
